import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/common/header";
import TabsComponent from "../components/dashboard/tabs";
import Search from "../components/dashboard/search";
import PaginationComponent from "../components/dashboard/pagination";
import BackToTop from "../components/common/backToTop";
import { get150Coins } from "../functions/get150Coins";

const FALLBACK_COINS = [
  coin("bitcoin", "btc", "Bitcoin", 64150, 1.2, 21053420042, 1287552679749, "https://assets.coingecko.com/coins/images/1/large/bitcoin.png"),
  coin("ethereum", "eth", "Ethereum", 1894.92, -0.5, 6484818255, 228676103537, "https://assets.coingecko.com/coins/images/279/large/ethereum.png"),
  coin("tether", "usdt", "Tether", 0.999, 0, 33177477072, 182976352011, "https://assets.coingecko.com/coins/images/325/large/Tether.png"),
  coin("binancecoin", "bnb", "BNB", 602.86, -0.2, 509214938, 80275003111, "https://assets.coingecko.com/coins/images/825/large/bnb-icon2_2x.png"),
  coin("usd-coin", "usdc", "USDC", 1, 0.01, 24168081297, 74000000000, "https://assets.coingecko.com/coins/images/6319/large/USD_Coin_icon.png"),
  coin("ripple", "xrp", "XRP", 1.49, 1.01, 5591760456, 88000000000, "https://assets.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png"),
  coin("solana", "sol", "Solana", 148.72, 2.4, 3220000000, 69000000000, "https://assets.coingecko.com/coins/images/4128/large/solana.png"),
  coin("tron", "trx", "TRON", 0.28, -0.4, 980000000, 24000000000, "https://assets.coingecko.com/coins/images/1094/large/tron-logo.png"),
];

function coin(id, symbol, name, current_price, price_change_percentage_24h, total_volume, market_cap, image) {
  return { id, symbol, name, current_price, price_change_percentage_24h, total_volume, market_cap, image };
}

function normalizeCoin(item) {
  const currentPrice = Number(item?.current_price);
  const priceChange = Number(item?.price_change_percentage_24h);
  const totalVolume = Number(item?.total_volume);
  const marketCap = Number(item?.market_cap);

  if (
    !item?.id ||
    !item?.symbol ||
    !item?.name ||
    !Number.isFinite(currentPrice) ||
    !Number.isFinite(priceChange) ||
    !Number.isFinite(totalVolume) ||
    !Number.isFinite(marketCap)
  ) {
    return null;
  }

  return {
    id: item.id,
    symbol: item.symbol,
    name: item.name,
    current_price: currentPrice,
    price_change_percentage_24h: priceChange,
    total_volume: totalVolume,
    market_cap: marketCap,
    image: item.image || "",
  };
}

function ClassicMarketTrackerPage() {
  const [coins, setCoins] = useState(FALLBACK_COINS);
  const [paginatedCoins, setPaginatedCoins] = useState(FALLBACK_COINS.slice(0, 10));
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [feedMode, setFeedMode] = useState("fallback");

  const filteredCoins = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return coins;
    return coins.filter(
      (item) =>
        item.name.toLowerCase().includes(value) ||
        item.symbol.toLowerCase().includes(value),
    );
  }, [coins, search]);

  useEffect(() => {
    let cancelled = false;

    async function getData() {
      try {
        const nextCoins = await get150Coins();
        const safeCoins = Array.isArray(nextCoins)
          ? nextCoins.map(normalizeCoin).filter(Boolean)
          : [];

        if (!cancelled && safeCoins.length >= 6) {
          setCoins(safeCoins);
          setPaginatedCoins(safeCoins.slice(0, 10));
          setFeedMode("live");
        }
      } catch (error) {
        if (!cancelled) {
          setFeedMode("fallback");
        }
      }
    }

    getData();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    const previousIndex = (value - 1) * 10;
    setPaginatedCoins(filteredCoins.slice(previousIndex, previousIndex + 10));
  };

  return (
    <>
      <Header />
      <BackToTop />
      <main className="classic-market-page">
        <div className="classic-feed-badge">
          <span>{feedMode === "live" ? "Live market feed" : "Fallback market feed"}</span>
        </div>
        <Search search={search} onSearchChange={onSearchChange} />
        <TabsComponent
          coins={search ? filteredCoins : paginatedCoins}
          setSearch={setSearch}
        />
        {!search && (
          <PaginationComponent page={page} handlePageChange={handlePageChange} />
        )}
      </main>
    </>
  );
}

export default ClassicMarketTrackerPage;
